import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { NOT_FOUND_EXPECTED_QUERY_META } from '../lib/query/queryErrorReporting';
import { billingApi } from './billingApi';
import { useAuthStore } from '../stores/authStore';
import type {
  BillingOverviewResponse,
  SubscriptionDto,
  CreditBalanceDto,
  PaymentMethodDto,
  PackageDto,
  PaymentTransactionPagedResponse,
  TopUpCreditRequest,
  TopUpCreditResponse,
  CancelSubscriptionRequest,
  SubscriptionPurchaseRequest,
  SubscriptionPurchaseResponse,
  AdminTenantBillingResponse,
  GrantCreditRequest,
  GrantCreditResponse,
  BillingAccessSummary,
  UpdateBillingPhoneRequest,
} from '../types/billing';
import { appCatalogApi, type AppCatalogRoadmapItemResponse } from './appCatalogApi';
import {
  auditLogApi,
  type AuditLogFilterParams,
  type AuditLogListResponse,
} from './auditLogApi';
import {
  catalogApi,
} from './catalogApi';
import {
  warehouseApi,
} from './warehouseApi';
import type { CategoryListItem } from '../types/admin';
import type { TenantAppStoreItem } from '../pages/tenant/appHelpers';
import type { PagedResponse } from '../types/paging';
import type {
  CategoryAttributeSchemaResponse,
  ProviderCategorySearchItem,
} from '../types/marketplace';

// ── Typed query-key factories ─────────────────────────────────────────────────

export const auditLogKeys = {
  all: (tenantId: string) => ['auditLogs', tenantId] as const,
  list: (tenantId: string, filters: AuditLogFilterParams) =>
    [...auditLogKeys.all(tenantId), 'list', filters] as const,
  detail: (tenantId: string, id: string) =>
    [...auditLogKeys.all(tenantId), 'detail', id] as const,
};

export const cashBankListKeys = {
  all: (tenantId: string) => ['cashBankList', tenantId] as const,
  list: (tenantId: string) => [...cashBankListKeys.all(tenantId), 'list'] as const,
  detail: (tenantId: string, id: string) =>
    [...cashBankListKeys.all(tenantId), 'detail', id] as const,
};

export const categoryKeys = {
  all: (tenantId: string) => ['categories', tenantId] as const,
  list: (tenantId: string, filters: { q?: string; page: number; pageSize: number }) =>
    [...categoryKeys.all(tenantId), 'list', filters] as const,
  dropdown: (tenantId: string) => [...categoryKeys.all(tenantId), 'dropdown'] as const,
  /**
   * AB#2021: magaza kapsamli katalog tutan pazaryerlerinde (Beecommerce) ayni provider'in iki
   * magazasi FARKLI kategori agaci dondurur, dolayisiyla storeId anahtarin parcasidir. Ayri bir
   * segment olarak durur; `${provider}:${storeId}` gibi birlestirilmis bir dize, iki alanin
   * ayrimini kaybeder ve degeri olmayan bir kacis kurali (ya varsayimi) getirirdi.
   */
  providerSearch: (
    tenantId: string,
    provider: string,
    q: string,
    storeId?: string | null,
  ) => [...categoryKeys.all(tenantId), 'providerSearch', provider, storeId ?? null, q] as const,
  /**
   * storeId is its own segment for the same reason as in `providerSearch`. `mappedTo` identifies
   * the integration category the row is mapped to, so changing or clearing the mapping lands on a
   * different entry instead of showing the previous category's schema.
   */
  attributeSchema: (
    tenantId: string,
    localCategoryId: string,
    provider: string,
    storeId: string | null,
    mappedTo: string,
  ) =>
    [...categoryKeys.all(tenantId), 'attributeSchema', localCategoryId, provider, storeId, mappedTo] as const,
};

export const deliveryNoteKeys = {
  all: (tenantId: string) => ['deliveryNotes', tenantId] as const,
  list: (
    tenantId: string,
    filters: { search: string; typeFilter: number | null; page: number; pageSize: number },
  ) => [...deliveryNoteKeys.all(tenantId), 'list', filters] as const,
};

export const tenantAppKeys = {
  all: (tenantId: string) => ['tenantApps', tenantId] as const,
  list: (tenantId: string) => [...tenantAppKeys.all(tenantId), 'list'] as const,
};

export const upcomingIntegrationKeys = {
  /** Global — not tenant-scoped; roadmap is platform-wide. */
  all: ['upcomingIntegrations'] as const,
  list: () => [...upcomingIntegrationKeys.all, 'list'] as const,
};

export const warehouseListKeys = {
  all: (tenantId: string) => ['warehouseList', tenantId] as const,
  dropdown: (tenantId: string) => [...warehouseListKeys.all(tenantId), 'dropdown'] as const,
  list: (
    tenantId: string,
    filters: { q: string; page: number; pageSize: number },
  ) => [...warehouseListKeys.all(tenantId), 'list', filters] as const,
};

// ── AuditLogsPage ─────────────────────────────────────────────────────────────

/** Paginated, filtered audit log list for AuditLogsPage. */
export function useAuditLogs(
  tenantId: string | undefined,
  filters: AuditLogFilterParams,
) {
  return useQuery<AuditLogListResponse>({
    queryKey: auditLogKeys.list(tenantId ?? '', filters),
    queryFn: ({ signal }) => auditLogApi.list(tenantId!, filters, signal),
    enabled: !!tenantId,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

/** Returns a stable function that invalidates all audit-log caches. */
export function useInvalidateAuditLogs(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: auditLogKeys.all(tenantId ?? '') }),
    [qc, tenantId],
  );
}

// ── CashBankPage ──────────────────────────────────────────────────────────────

/** Full cash/bank account list for CashBankPage management view (always loads). */
export function useCashBankList(tenantId: string | undefined) {
  return useQuery({
    queryKey: cashBankListKeys.list(tenantId ?? ''),
    queryFn: ({ signal }) =>
      api
        .get(`/api/v1/tenants/${tenantId}/cash-bank-accounts`, {
          params: { pageSize: 500 },
          signal,
        })
        .then((r) => (r.data?.items ?? r.data ?? []) as CashBankAccountListItem[]),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/** Single cash/bank account detail; the detail type is supplied by the caller (movements live in the page). */
export function cashBankDetailQuery<TDetail extends CashBankAccountListItem = CashBankAccountListItem>(
  tenantId: string,
  id: string,
) {
  return {
    queryKey: cashBankListKeys.detail(tenantId, id),
    queryFn: ({ signal }: { signal: AbortSignal }): Promise<TDetail> =>
      api
        .get(`/api/v1/tenants/${tenantId}/cash-bank-accounts/${id}`, { signal })
        .then((r) => r.data),
  };
}

/** Returns a stable function that invalidates the cash/bank list cache (list and detail). */
export function useInvalidateCashBankList(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: cashBankListKeys.all(tenantId ?? '') }),
    [qc, tenantId],
  );
}

// ── CategoriesPage ────────────────────────────────────────────────────────────

/** Paginated, filtered category list for CategoriesPage. */
export function useCategoryList(
  tenantId: string | undefined,
  filters: { q?: string; page: number; pageSize: number },
) {
  return useQuery({
    queryKey: categoryKeys.list(tenantId ?? '', filters),
    queryFn: ({ signal }) =>
      catalogApi.getCategoriesPaged(
        tenantId!,
        {
          q: filters.q?.trim() || undefined,
          page: filters.page,
          pageSize: filters.pageSize,
        },
        signal,
      ),
    enabled: !!tenantId,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

/** All categories for parent dropdown — up to 1000, cached 5 min. */
export function useCategoryDropdown(tenantId: string | undefined) {
  return useQuery<CategoryListItem[]>({
    queryKey: categoryKeys.dropdown(tenantId ?? ''),
    queryFn: ({ signal }) => catalogApi.getAllCategories(tenantId!, signal),
    enabled: !!tenantId,
    staleTime: 300_000,
  });
}

/**
 * Marketplace provider category search — fires when provider + q are non-empty.
 *
 * AB#2021: magaza kapsamli katalog tutan pazaryerlerinde (Beecommerce) backend `storeId`
 * ister; kategori id uzayi kurulum basina degistigi icin provider geneli arama yanlis
 * magazanin agacini onerirdi. Provider geneli pazaryerlerinde parametre gonderilmez.
 */
export function useProviderCategorySearch(
  tenantId: string | undefined,
  provider: string | null,
  q: string,
  storeId?: string | null,
) {
  return useQuery({
    queryKey: categoryKeys.providerSearch(tenantId ?? '', provider ?? '', q, storeId),
    queryFn: ({ signal }) =>
      api
        .get<ProviderCategorySearchItem[]>(
          `/api/v1/tenants/${tenantId}/marketplace-providers/${provider}/categories/search`,
          {
            params: { q, leafOnly: true, limit: 20, ...(storeId ? { storeId } : {}) },
            signal,
          },
        )
        .then((r) => r.data),
    enabled: !!tenantId && !!provider && q.trim().length > 0,
    staleTime: 60_000,
    // The 404 (store deactivated/removed) is a reason the dialog shows, not a defect to report.
    meta: NOT_FOUND_EXPECTED_QUERY_META,
  });
}

/**
 * Integration-side attribute schema of the category a local category is mapped to. The caller
 * mounts it only while the provider row is expanded, so opening the mapping panel does not issue
 * one request per provider. `storeId` is sent only for store-scoped rows. staleTime is a short
 * 60s: a notSynced schema becomes ready once the background sync ran, so re-expanding the row
 * after that window refetches while still showing the cached value. The 404 (store removed) is a reason shown inline.
 */
export function useCategoryAttributeSchema(
  tenantId: string | undefined,
  localCategoryId: string,
  provider: string,
  storeId: string | null,
  mappedTo: string,
) {
  return useQuery<CategoryAttributeSchemaResponse>({
    queryKey: categoryKeys.attributeSchema(tenantId ?? '', localCategoryId, provider, storeId, mappedTo),
    queryFn: ({ signal }) =>
      api
        .get<CategoryAttributeSchemaResponse>(
          `/api/v1/tenants/${tenantId}/local-categories/${localCategoryId}/marketplace-mappings/provider/${provider}/attributes`,
          { params: storeId ? { storeId } : undefined, signal },
        )
        .then((r) => r.data),
    enabled: !!tenantId,
    staleTime: 60_000,
    meta: NOT_FOUND_EXPECTED_QUERY_META,
  });
}

/** Returns a stable function that invalidates all category caches. */
export function useInvalidateCategories(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: categoryKeys.all(tenantId ?? '') }),
    [qc, tenantId],
  );
}

// ── DeliveryNotesPage ─────────────────────────────────────────────────────────

interface DeliveryNoteListItem {
  id: string;
  noteNumber: string;
  type: number;
  status: number;
  noteDate: string;
  recipientName: string;
  invoiceId: string | null;
  invoiceNumber: string | null;
  lineCount: number;
  createdAt: string;
}

interface DeliveryNoteListResponse {
  items: DeliveryNoteListItem[];
  totalCount: number;
}

/** Paginated, filtered delivery-note list for DeliveryNotesPage. */
export function useDeliveryNoteList(
  tenantId: string | undefined,
  filters: { search: string; typeFilter: number | null; page: number; pageSize: number },
) {
  return useQuery<DeliveryNoteListResponse>({
    queryKey: deliveryNoteKeys.list(tenantId ?? '', filters),
    queryFn: ({ signal }) => {
      const p = new URLSearchParams();
      if (filters.search) p.set('q', filters.search);
      if (filters.typeFilter) p.set('type', String(filters.typeFilter));
      p.set('page', String(filters.page));
      p.set('pageSize', String(filters.pageSize));
      return api
        .get(`/api/v1/tenants/${tenantId}/delivery-notes?${p}`, { signal })
        .then((r) => r.data as DeliveryNoteListResponse);
    },
    enabled: !!tenantId,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

/** Returns a stable function that invalidates all delivery-note list caches. */
export function useInvalidateDeliveryNotes(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: deliveryNoteKeys.all(tenantId ?? '') }),
    [qc, tenantId],
  );
}

// ── TenantAppsPage ────────────────────────────────────────────────────────────

/** Full app store list for TenantAppsPage (new 3-gate model). */
export function useTenantApps(tenantId: string | undefined) {
  return useQuery<TenantAppStoreItem[]>({
    queryKey: tenantAppKeys.list(tenantId ?? ''),
    queryFn: ({ signal }) =>
      api
        .get(`/api/v1/tenants/${tenantId}/apps`, { signal })
        .then((r) => r.data as TenantAppStoreItem[]),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/** Returns a stable function that invalidates the tenant apps list cache. */
export function useInvalidateTenantApps(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: tenantAppKeys.all(tenantId ?? '') }),
    [qc, tenantId],
  );
}

/**
 * Yakininda gelen entegrasyonlarin yol haritasini cekip dondurur.
 * GET /api/v1/app-catalog/roadmap — TenantAccess.
 * Tum sayfalari tek sorguda cekmek yerine ilk sayfada yeterli (max 100 kayit).
 * Roadmap degisimi nadirdir; staleTime 5 dakika olarak ayarlandi.
 */
export function useUpcomingIntegrations() {
  return useQuery<AppCatalogRoadmapItemResponse[]>({
    queryKey: upcomingIntegrationKeys.list(),
    queryFn: ({ signal }) =>
      appCatalogApi.getRoadmap(1, 100, signal).then((r) => r.items),
    staleTime: 5 * 60_000,
  });
}

// ── WarehousesPage ────────────────────────────────────────────────────────────

/** Paginated, filtered warehouse list for WarehousesPage. */
export function useWarehouseList(
  tenantId: string | undefined,
  filters: { q: string; page: number; pageSize: number },
) {
  return useQuery({
    queryKey: warehouseListKeys.list(tenantId ?? '', filters),
    queryFn: ({ signal }) =>
      warehouseApi.getWarehousesPaged(tenantId!, {
        q: filters.q,
        page: filters.page,
        pageSize: filters.pageSize,
      }, signal),
    enabled: !!tenantId,
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
}

/** Returns a stable function that invalidates all warehouse list caches. */
export function useInvalidateWarehouseList(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: warehouseListKeys.all(tenantId ?? '') }),
    [qc, tenantId],
  );
}

/** All warehouses for form dropdowns (order / quote forms). Cached 5 min. */
export function useFormWarehouses(tenantId: string | undefined) {
  return useQuery<{ id: string; name: string }[]>({
    queryKey: warehouseListKeys.dropdown(tenantId ?? ''),
    queryFn: ({ signal }) =>
      warehouseApi
        .getAllWarehouses(tenantId!, signal)
        .then((items) => items.map((w) => ({ id: w.id, name: w.name }))),
    enabled: !!tenantId,
    staleTime: 300_000,
  });
}

// ── Local types for cash/bank management (not in shared types) ────────────────

export interface CashBankAccountListItem {
  id: string;
  code: string;
  name: string;
  kind: number;
  bankName: string | null;
  accountNumber: string | null;
  iban: string | null;
  currency: string;
  openingBalance: number;
  totalIn: number;
  totalOut: number;
  balance: number;
  isActive: boolean;
  createdAt: string;
  companyId: string | null;
}

// ── Retention Exemption Requests (tenant side — K6 §7.2) ─────────────────────

export interface TenantRetentionExemptionItem {
  id: string;
  appCode: string;
  requestedReason: string;
  status: 'Pending' | 'Approved' | 'Rejected' | 'Expired';
  decisionNote: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

export interface CreateTenantRetentionExemptionRequest {
  appCode: string;
  requestedReason: string;
}

export const retentionExemptionKeys = {
  all:  (tenantId: string) => ['retentionExemptions', tenantId] as const,
  list: (tenantId: string) => [...retentionExemptionKeys.all(tenantId), 'list'] as const,
};

/** Fetch the tenant's own retention exemption requests. */
export function useTenantRetentionExemptions(tenantId: string | undefined) {
  // Backend returns a paged envelope { items, total, page, pageSize } — extract items
  // so consumers get a plain array (a bare-array assumption here crashed TenantAppsPage
  // with "exemptionRequests.find is not a function").
  return useQuery<TenantRetentionExemptionItem[]>({
    queryKey: retentionExemptionKeys.list(tenantId ?? ''),
    queryFn: ({ signal }) =>
      api
        .get<{ items: TenantRetentionExemptionItem[] }>(
          `/api/v1/tenants/${tenantId}/retention-exemptions`,
          { signal },
        )
        .then((r) => r.data.items ?? []),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

/** Submit a new retention exemption request for a locked app. */
export function useCreateTenantRetentionExemption(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTenantRetentionExemptionRequest) =>
      api
        .post<{ id: string }>(`/api/v1/tenants/${tenantId}/retention-exemptions`, body)
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: retentionExemptionKeys.list(tenantId) });
    },
    meta: { errorToast: true },
  });
}

// Keep PagedResponse re-export for consumers that need it alongside these hooks.
export type { PagedResponse };
// Re-export ProviderCategorySearchItem so CategoriesPage can import from one place.
export type { ProviderCategorySearchItem };

// ── Billing (T5b) ─────────────────────────────────────────────────────────────

export const billingKeys = {
  all:           (tenantId: string) => ['billing', tenantId] as const,
  overview:      (tenantId: string) => [...billingKeys.all(tenantId), 'overview'] as const,
  subscriptions: (tenantId: string) => [...billingKeys.all(tenantId), 'subscriptions'] as const,
  credits:       (tenantId: string) => [...billingKeys.all(tenantId), 'credits'] as const,
  paymentMethods:(tenantId: string) => [...billingKeys.all(tenantId), 'paymentMethods'] as const,
  packages:      (tenantId: string) => [...billingKeys.all(tenantId), 'packages'] as const,
  access:        (tenantId: string) => [...billingKeys.all(tenantId), 'access'] as const,
  transactions:  (
    tenantId: string,
    page: number,
    pageSize: number,
  ) => [...billingKeys.all(tenantId), 'transactions', page, pageSize] as const,
  adminOverview: (tenantId: string) => ['adminBilling', tenantId] as const,
};

/** Billing overview (subscriptions + credits + default payment method). */
export function useBillingOverview(tenantId: string | undefined) {
  return useQuery<BillingOverviewResponse>({
    queryKey: billingKeys.overview(tenantId ?? ''),
    queryFn: ({ signal }) => billingApi.getOverview(tenantId!, signal),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/** All subscriptions for the tenant. */
export function useBillingSubscriptions(tenantId: string | undefined) {
  return useQuery<SubscriptionDto[]>({
    queryKey: billingKeys.subscriptions(tenantId ?? ''),
    queryFn: ({ signal }) => billingApi.getSubscriptions(tenantId!, signal),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/** All credit balances for the tenant. */
export function useBillingCredits(tenantId: string | undefined) {
  return useQuery<CreditBalanceDto[]>({
    queryKey: billingKeys.credits(tenantId ?? ''),
    queryFn: ({ signal }) => billingApi.getCredits(tenantId!, signal),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/** Available billing packages (bundles) for the tenant. */
export function useBillingPackages(tenantId: string | undefined) {
  return useQuery<PackageDto[]>({
    queryKey: billingKeys.packages(tenantId ?? ''),
    queryFn: ({ signal }) => billingApi.getPackages(tenantId!, signal),
    enabled: !!tenantId,
    staleTime: 60_000,
  });
}

/**
 * Access-gate summary (Entitled/Blocked). Short staleTime — server itself caches this for 30s
 * (docs/design/billing-access-gate.md), so a tighter client staleTime buys nothing but a query
 * this cheap can afford to just track the server's own freshness window rather than pretend to
 * be stale-while-revalidate on top of it.
 *
 * `options` lets the two callers that need to actively poll this cheap endpoint opt in without a
 * second hook: `SubscriptionGuard` polls every 60s while the tenant is Blocked (a TenantMember
 * never lands on a purchase-success refresh, so this is the only way their guard lifts without a
 * fresh login), and `BillingReturnPage` polls every 3s while waiting for a just-completed
 * purchase to land. `enabled` composes with (never overrides) the existing `!!tenantId` guard.
 */
export function useBillingAccess(
  tenantId: string | undefined,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery<BillingAccessSummary>({
    queryKey: billingKeys.access(tenantId ?? ''),
    queryFn: ({ signal }) => billingApi.getAccess(tenantId!, signal),
    enabled: !!tenantId && (options?.enabled ?? true),
    staleTime: 30_000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Returns a stable callback that re-checks `billing/access` and pushes the result into
 * authStore — the same store field SubscriptionGuard reads. Called right after a DORMANT
 * (chargeRequired=false) purchase succeeds (BillingPackageCard, SubscribePlanDialog) and after
 * a successful hosted-payment redirect returns (BillingReturnPage): both are moments where the
 * tenant's access may have just flipped Entitled, and waiting for the next login/refresh
 * round-trip would leave the full-screen block up for no reason.
 *
 * Deliberately bypasses the query cache (no `useBillingAccess` / `queryClient.setQueryData`
 * here) — this call must land in authStore, and TanStack Query has no built-in way to run a
 * callback off a cache write, so a direct `billingApi.getAccess` + `setBillingAccess` is the
 * straight line to the field the guard actually reads.
 */
export function useRefreshBillingAccess(tenantId: string | undefined) {
  const setBillingAccess = useAuthStore((s) => s.setBillingAccess);
  return useCallback(async () => {
    if (!tenantId) return null;
    const summary = await billingApi.getAccess(tenantId);
    setBillingAccess(summary);
    return summary;
  }, [tenantId, setBillingAccess]);
}

/** All saved payment methods for the tenant. */
export function useBillingPaymentMethods(tenantId: string | undefined) {
  return useQuery<PaymentMethodDto[]>({
    queryKey: billingKeys.paymentMethods(tenantId ?? ''),
    queryFn: ({ signal }) => billingApi.getPaymentMethods(tenantId!, signal),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/** Paged payment transaction history. */
export function useBillingTransactions(
  tenantId: string | undefined,
  page: number,
  pageSize: number,
) {
  return useQuery<PaymentTransactionPagedResponse>({
    queryKey: billingKeys.transactions(tenantId ?? '', page, pageSize),
    queryFn: ({ signal }) => billingApi.getTransactions(tenantId!, page, pageSize, signal),
    enabled: !!tenantId,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

/** Top up credits for an app. Idempotent via IdempotencyKey. */
export function useTopUpCredit(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<TopUpCreditResponse, Error, { appCode: string; req: TopUpCreditRequest }>({
    mutationFn: ({ appCode, req }) => billingApi.topUpCredit(tenantId, appCode, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.credits(tenantId) });
      qc.invalidateQueries({ queryKey: billingKeys.overview(tenantId) });
      qc.invalidateQueries({ queryKey: billingKeys.transactions(tenantId, 1, 10) });
    },
    // TopUpCreditDialog renders the rejection inline (setServerError).
    meta: { errorInline: true },
  });
}

/**
 * Purchase a new subscription (hosted-payment or dormant). Idempotent via IdempotencyKey.
 *
 * The error channel is the caller's to choose because this hook's three consumers do not
 * share one: `SubscribePlanDialog` renders the rejection in its own inline `Alert`, while
 * `BillingPackageCard` and `BillingPage` are plain action buttons with nowhere to put it.
 * A single hook-level declaration is wrong for two of the three whichever way it is set, and
 * TanStack's per-call `mutate(v, { onError })` cannot express it — that callback never reaches
 * `mutation.options`, which is what made the AB#1749 default silently double-report here.
 */
export function usePurchaseSubscription(
  tenantId: string,
  errorChannel: 'toast' | 'inline' = 'toast',
) {
  const qc = useQueryClient();
  return useMutation<SubscriptionPurchaseResponse, Error, SubscriptionPurchaseRequest>({
    mutationFn: (req) => billingApi.purchaseSubscription(tenantId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.subscriptions(tenantId) });
      qc.invalidateQueries({ queryKey: billingKeys.overview(tenantId) });
    },
    meta: errorChannel === 'inline' ? { errorInline: true } : { errorToast: true },
  });
}

/** Toggle CancelAtPeriodEnd on a subscription. */
export function useSetCancelAtPeriodEnd(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { subscriptionId: string; req: CancelSubscriptionRequest }
  >({
    mutationFn: ({ subscriptionId, req }) =>
      billingApi.cancelSubscription(tenantId, subscriptionId, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.subscriptions(tenantId) });
      qc.invalidateQueries({ queryKey: billingKeys.overview(tenantId) });
    },
    meta: { errorToast: true },
  });
}

/** Returns a stable function that invalidates all billing caches for the tenant. */
export function useInvalidateBilling(tenantId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    () => qc.invalidateQueries({ queryKey: billingKeys.all(tenantId ?? '') }),
    [qc, tenantId],
  );
}

/** Admin read-only view: SuperAdmin billing overview for a specific tenant. */
export function useAdminTenantBilling(tenantId: string | undefined) {
  return useQuery<AdminTenantBillingResponse>({
    queryKey: billingKeys.adminOverview(tenantId ?? ''),
    queryFn: ({ signal }) => billingApi.adminGetTenantBilling(tenantId!, signal),
    enabled: !!tenantId,
    staleTime: 30_000,
  });
}

/**
 * SuperAdmin credit grant for one app of a tenant. Refreshes the admin view (new balance +
 * the ADMIN_GRANT movement) and the tenant's own billing family so the tenant sees the credits
 * the next time that cache is read. The dialog renders the rejection inline.
 */
export function useAdminGrantCredit(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<GrantCreditResponse, Error, { appCode: string; req: GrantCreditRequest }>({
    mutationFn: ({ appCode, req }) => billingApi.adminGrantCredit(tenantId, appCode, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: billingKeys.adminOverview(tenantId) });
      qc.invalidateQueries({ queryKey: billingKeys.all(tenantId) });
    },
    // A failed response may still have committed (lost response), so refresh the shown balance.
    onError: () => {
      qc.invalidateQueries({ queryKey: billingKeys.adminOverview(tenantId) });
    },
    meta: { errorInline: true },
  });
}

/**
 * Sets the tenant's phone (AB#2193 billing phone-required flow) — the one write a TenantAdmin
 * can make themselves, distinct from SuperAdmin's full-profile `adminApi.updateTenant`.
 * `PUT .../billing/phone` deliberately lives under the `billing` sub-router (TenantAdminOnly +
 * Billing.Write) rather than a general tenant-profile route, so a Blocked tenant can still call
 * it without tripping the 402 access gate. First consumer: BillingPhonePromptDialog.
 */
export function useUpdateBillingPhone(tenantId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, UpdateBillingPhoneRequest>({
    mutationFn: (request) => billingApi.updateBillingPhone(tenantId, request),
    onSuccess: () => {
      // Nothing else in the app currently caches the tenant's phone, but invalidating the whole
      // billing family costs nothing and keeps this correct the day a screen reads it back.
      qc.invalidateQueries({ queryKey: billingKeys.all(tenantId) });
    },
    // BillingPhonePromptDialog renders the rejection inline.
    meta: { errorInline: true },
  });
}

// Re-export types so billing UI only needs one import.
export type {
  BillingOverviewResponse,
  SubscriptionDto,
  CreditBalanceDto,
  PaymentMethodDto,
  PaymentTransactionDto,
  PaymentTransactionPagedResponse,
  TopUpCreditRequest,
  TopUpCreditResponse,
  CancelSubscriptionRequest,
  SubscriptionPurchaseRequest,
  SubscriptionPurchaseResponse,
  BillingPeriod,
  AdminTenantBillingResponse,
  GrantCreditRequest,
  GrantCreditResponse,
} from '../types/billing';
